/**
 * Allowed field values for GWMS API (v2 Joi + v2.1 Zod).
 * Must stay 1:1 with apps/gwms v2 Joi validators and Core column constraints.
 */

/** LINE_STATE column on order/claim/return/receiving lines. */
export const LINE_STATE_VALUES = ['E', 'D'] as const;

/** orders.ORDER_TYPE — GWMS v2 also accepts uppercase B2C/B2B on input. */
export const ORDER_TYPE_VALUES = ['b2c', 'b2b', 'transfer', 'B2C', 'B2B'] as const;

/** Return write body only (no transfer). */
export const RETURN_ORDER_TYPE_VALUES = ['b2c', 'b2b', 'transfer'] as const;

/** orders.PAYMENT_METHOD — string digits in v2/v21 wire format. */
export const PAYMENT_METHOD_VALUES = ['0', '1', '2'] as const;

export const MARKETPLACE_VALUES = ['alza', 'allegro', 'kaufland', 'mall', 'other'] as const;

export const CUSTOMS_PRODUCT_TYPE_VALUES = [
    'gift',
    'documents',
    'saleOfGoods',
    'commercialSample',
    'returnedGoods',
] as const;

export const ORDER_FILE_TYPE_VALUES = ['invoice', 'shippingAddonLabel', 'shipResponse', 'other'] as const;

export const ORDER_PRODUCT_FILE_TYPE_VALUES = ['other', 'engravingType'] as const;

export const ORDER_FILE_FORMAT_VALUES = ['PDF', 'ZPL'] as const;

export const CLAIM_CAUSE_VALUES = [
    'lost',
    'damaged',
    'deliveryTimeExceeded',
    'others',
    'trackingLabelsSwapped',
    'duplicatedLabels',
    'missingItems',
    'exceedingItems',
    'swappedItems',
] as const;

export const CLAIM_RESOLVER_VALUES = ['skladon', 'carrier', 'skladonIT'] as const;

export const CLAIM_STATUS_VALUES = [
    'received',
    'inProcess',
    'accepted',
    'rejected',
    'storno',
    'acceptedWaitingForCompensationFromShipper',
    'moreInfoNeeded',
    'informationAdded',
    'acceptedPendingClientCompensation',
] as const;

export const RETURN_REASON_VALUES = [
    'exchange',
    'nofit',
    'changemind',
    'nolike',
    'undeliverable',
    'late',
    'damaged',
    'unknown',
] as const;

export const RETURN_STATUS_VALUES = ['awaiting', 'received', 'confirmed', 'canceled'] as const;

/** Read-only on return lines — Core resolution codes returned by API. */
export const RETURN_RESOLUTION_VALUES = [
    'returnToInventory',
    'receiveAsDamaged',
    'restoredToOriginal',
    'failedRestoredToOriginal',
    'differentLook',
    'disposal',
    'dryCleaning',
    'signsOfUse',
] as const;

export const RECEIVING_TYPE_VALUES = [
    'piece_mix',
    'carton_single',
    'carton_mix',
    'pallet_single',
    'pallet_mix',
] as const;

export const INCOTERMS_VALUES = ['FOB', 'CPT', 'CIP', 'DPU', 'DAP', 'DDP'] as const;

export const RECEIVING_STATUS_VALUES = ['awaiting', 'unloaded', 'received', 'confirmed', 'canceled'] as const;

export const RECEIVING_URGENT_VALUES = ['ok', 'afterUrgent', 'recievedWithoutAsn'] as const;

export const RECEIVING_PRIORITY_VALUES = [0, 1, 2] as const;

export const INSERT_TYPE_VALUES = ['everyOrder', 'specificProduct'] as const;

export const GRID_FILTER_OPERATOR_VALUES = [
    'eq',
    'neq',
    'like',
    'nlike',
    'gt',
    'lt',
    'gte',
    'lte',
    'in',
    'nin',
    'between',
    'nbetween',
    'null',
    'notnull',
    'empty',
    'notempty',
    'likein',
] as const;

/**
 * Meaning of each grid filter operator. Values themselves are the OpenAPI enum —
 * do not append "Allowed values: …" here (Zudoku already lists the enum).
 *
 * Soft newlines collapse in Zudoku schema Markdown; lists get flattened. Use
 * explicit <br> (rehype-raw) so each operator stays on its own line.
 */
export const GRID_FILTER_OPERATOR_DESCRIPTION = [
    'Filter operator:',
    '`eq` — equal',
    '`neq` — not equal',
    '`like` — substring match',
    '`nlike` — not like',
    '`likein` — like any of (value is an array of strings)',
    '`gt` — greater than',
    '`gte` — greater than or equal',
    '`lt` — less than',
    '`lte` — less than or equal',
    '`in` — value is in the given array',
    '`nin` — value is not in the given array',
    '`between` — value is in the closed interval (array of two bounds)',
    '`nbetween` — value is outside that interval',
    '`null` — field is null',
    '`notnull` — field is not null',
    '`empty` — field is null or empty string',
    '`notempty` — field is neither null nor empty string',
].join('<br>\n');

export const ORDER_STATUS_VALUES = [
    'inProcess',
    'lost',
    'received',
    'processing',
    'processed',
    'onTheWay',
    'atPickupPoint',
    'delivered',
    'returnedBack',
    'canceled',
    'returningBack',
    'onResend',
    'refundInProcess',
    'receivedBack',
    'partlyReceivedBack',
    'preReceived',
    'preOrder',
    'incorrect',
    'suspended',
    'waitingForPayment',
    'waitingForShipment',
    'productsNotAvailable',
    'packed',
    'waitingForCarrier',
] as const;

/** Statuses accepted on order PUT/PATCH write (same for both methods). */
export const ORDER_STATUS_WRITE_VALUES = [
    'preReceived',
    'preOrder',
    'waitingForPayment',
    'productsNotAvailable',
    'waitingForShipment',
    'suspended',
    'incorrect',
    'canceled',
    'onResend',
] as const;

/** @deprecated Use ORDER_STATUS_WRITE_VALUES — PUT and PATCH share the same write enum. */
export const PUT_ORDER_STATUS_WRITE_VALUES = ORDER_STATUS_WRITE_VALUES;

/** @deprecated Use ORDER_STATUS_WRITE_VALUES — PUT and PATCH share the same write enum. */
export const PATCH_ORDER_STATUS_WRITE_VALUES = ORDER_STATUS_WRITE_VALUES;

/** Product types accepted on create / PUT / PATCH. */
export const PRODUCT_TYPE_WRITE_VALUES = [
    'pack',
    'packagingMaterial',
    'semiProduct',
    'finalProduct',
    'promoMaterial',
    'serviceProduct',
    'pallet',
    'virtual',
] as const;

/** Product types on GET / response — write values plus legacy `item`. */
export const PRODUCT_TYPE_VALUES = [
    'pack',
    'packagingMaterial',
    'semiProduct',
    'finalProduct',
    'promoMaterial',
    'serviceProduct',
    'pallet',
    'virtual',
    'item',
] as const;


/**
 * Carrier names for `driverCompany`. Mirrors MySkladon `constants_codes` (TYPE `driverCompanyCodes`) VALUEs;
 * a core test keeps the DB and this list in sync. Recommended values only — other values are not rejected.
 */
export const DRIVER_COMPANY_VALUES = [
    'Alza',
    'Aramex',
    'Cargo',
    'Česká pošta',
    'Dachser',
    'DB Schenker',
    'DHL',
    'DPD',
    'FedEx',
    'Geis',
    'GLS',
    'PPL',
    'Skladon',
    'TNT',
    'TOPTRANS',
    'UPS',
    'Vlastní / Custom',
    'WE|DO',
    'Zásilkovna',
    'other',
] as const;
